<template>
  <modal-vue @close="handleToggleModal" :modalActive="modals[modalName]" :hasFooterBorder="true">
    <template #header>
      <div class="contents px-[16px] flex">
        <strong>Removendo plantões</strong>
      </div>
    </template>
    <div>
      <p class="text-base font-normal">
        Tem certeza que deseja excluir {{ totalItemsToDelete }}
        {{ totalItemsToDelete === 1 ? 'plantão' : 'plantões' }}?
        <br />
        Essa ação não poderá ser desfeita!
      </p>

      <!-- Trava do Pós-Plantão (RN-07 / RF-12). Banner inline, não toast: o
           usuário precisa reler a contagem para refazer a seleção, e toast
           desaparece. Mesma iconografia do banner do modal de edição de
           plantão. -->
      <div
        v-if="postStationLockMessage"
        class="flex items-start gap-2 px-3 py-2 mt-4 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary text-left"
        data-testid="remove-bulk-station-post-station-lock-banner"
      >
        <SVGIcon :path="mdiLock" class="w-4 h-4 mt-0.5 shrink-0 text-primary30" />
        <span>{{ postStationLockMessage }}</span>
      </div>

      <div v-if="reasonFields.length" class="flex flex-col gap-4 mt-4">
        <ChangeReasonField
          v-for="(field, index) in reasonFields"
          :key="field.kind"
          v-model="reasonValues[field.kind]"
          v-bind="field"
          :show-errors="showReasonErrors"
          :scope-note="index === reasonFields.length - 1 ? reasonScopeNote : ''"
        />
      </div>
    </div>

    <template #footer>
      <div class="mt-5 flex justify-center gap-4">
        <outlined-button
          width="119px"
          data-testid="remove-bulk-station-cancel-button"
          @click="handleToggleModal"
          text="Cancelar"
        />
        <filled-button
          :loading="isLoading"
          text="Excluir"
          width="102px"
          data-testid="remove-bulk-station-confirm-button"
          @click="handleRemove"
        />
      </div>
    </template>
  </modal-vue>
</template>

<script lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiLock } from '@mdi/js'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import Network from '@/Network'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import ChangeReasonField from '@/components/ChangeReason/ChangeReasonField.vue'
import { useChangeReasonForm } from '@/composables/useChangeReasonForm'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import { POST_STATION_LOCK_BANNER_TEXT } from '@/modules/company/PostStation/constants'
import {
  getPostStationApprovedDoctorStationIds,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'
import {
  getStationCheckInLockMessage,
  isStationCheckInLocked
} from '@/modules/company/Dashboard/modals/stationErrors'
import {
  CHANGE_REASON_LABELS,
  CHANGE_REASON_TITLES,
  DELETION_REASON_OPTIONS,
  changeReasonScopeNote
} from '@/utils/changeReasons'

/** Fallback da trava por perna quando o backend não manda a frase pronta. */
const BULK_LEG_LOCK_FALLBACK =
  'Um ou mais plantões têm check-in ou check-out registrado pelo aplicativo ou validado na ' +
  'auditoria e não podem ser excluídos. Nada foi excluído.'

export default {
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    infos: {
      type: Array<number>
    },
    ids: {
      type: Array<number>
    },
    isSelectAll: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      required: true
    },
    totalItemsToDelete: {
      type: Number,
      required: true
    }
  },
  components: {
    ModalVue,
    FilledButton,
    OutlinedButton,
    SVGIcon,
    ChangeReasonField
  },
  setup(props, { emit }) {
    /* ******** Modal Props and Actions Default *********** */
    const modalName = EModals.REMOVE_BULK_STATION
    const { modals } = storeToRefs(useModalStore())
    const modalStore = useModalStore()
    const handleToggleModal = () => {
      modalStore.toggleModal(modalName)
    }
    /* **************************************************** */

    const isLoading = ref(false)

    const {
      values: reasonValues,
      showErrors: showReasonErrors,
      fields: reasonFields,
      reset: resetReasons,
      validate: validateReasons,
      build: buildReasons
    } = useChangeReasonForm(() => [
      {
        kind: 'deletion',
        title: CHANGE_REASON_TITLES.deletion(props.totalItemsToDelete),
        label: CHANGE_REASON_LABELS.deletion,
        options: DELETION_REASON_OPTIONS
      }
    ])
    const reasonScopeNote = computed(() =>
      changeReasonScopeNote(props.totalItemsToDelete, 'selecionados')
    )
    // Toda abertura do modal começa sem motivo (o componente fica montado na Consulta).
    watch(
      () => modals.value[modalName],
      (open) => {
        if (open) resetReasons()
      }
    )

    /**
     * Mensagem da trava do Pós-Plantão (RN-07 / RF-12).
     *
     * Aqui a trava é 100% reativa ao 400 do backend, não preventiva: são N
     * plantões vindos de seleção manual ou de filtro (`isSelectAll`, em que a UI
     * nem tem os ids), então sondar a avaliação de cada um é inviável.
     *
     * A exclusão em massa é atômica no backend (o validator acumula todos os
     * aprovados e lança um único 400 antes de qualquer escrita), por isso a
     * frase diz "Nenhum plantão foi excluído" — nunca "X falharam".
     */
    const postStationLockMessage = ref<string | null>(null)

    /* ******** Form Submited  *********** */
    const toast = useToast()
    const { notifyError, notifyErrorMessage } = useErrorToast()

    const buildLockMessage = (error: unknown) => {
      const blockedCount = getPostStationApprovedDoctorStationIds(error).length

      // `bulkDeleteByFilter` não devolve os ids (a UI não os enviou), só a
      // contagem some — daí a variante curta.
      const detail = blockedCount
        ? `${blockedCount} de ${props.totalItemsToDelete} estão aprovados para Pós-Plantão.`
        : 'Há plantões aprovados para Pós-Plantão na seleção.'

      return `Nenhum plantão foi excluído: ${detail} ${POST_STATION_LOCK_BANNER_TEXT}.`
    }

    const handleRemove = async () => {
      if (!validateReasons()) return
      const changeReasons = buildReasons()
      postStationLockMessage.value = null

      try {
        isLoading.value = true
        if (props.isSelectAll) {
          const correctedFilters = {
            ...props.filters,
            company: Number(props.filters.company)
          }
          await Network.station.bulkDeleteByFilter(correctedFilters, changeReasons)
        } else {
          if (!props.ids || props.ids.length === 0) {
            // Fecha como antes: o `finally` que fazia isso deixou de fechar,
            // porque agora só o sucesso (e o erro genérico) fecham o modal.
            handleToggleModal()
            return
          }
          await Network.station.bulkDelete(props.ids, changeReasons)
        }

        toast.success('Plantões removidos com sucesso')
        emit('refreshData')
        handleToggleModal()
      } catch (error) {
        // Modal permanece aberto: o usuário precisa da contagem para refazer a
        // seleção sem os plantões aprovados.
        if (isPostStationApprovedError(error)) {
          postStationLockMessage.value = buildLockMessage(error)
          return
        }

        // Trava por perna (backend #3055): tudo-ou-nada, a frase do backend diz
        // o que fazer (desmarcar / ajustar o filtro) — repassada, nunca o código cru.
        // Fecha o modal: a correção é refazer a seleção fora dele.
        if (isStationCheckInLocked(error)) {
          notifyErrorMessage(getStationCheckInLockMessage(error, BULK_LEG_LOCK_FALLBACK))
          handleToggleModal()
          return
        }

        // Antes: interpolava o erro técnico cru no toast ("Erro ao remover
        // plantões: Error: ..."). A mensagem do ServiceError chega limpa.
        notifyError(error, 'Não foi possível remover os plantões')
        handleToggleModal()
      } finally {
        isLoading.value = false
      }
    }
    /* **************************************************** */

    return {
      modalName,
      modals,
      isLoading,
      mdiLock,
      postStationLockMessage,
      reasonValues,
      showReasonErrors,
      reasonFields,
      reasonScopeNote,
      handleToggleModal,
      handleRemove
    }
  }
}
</script>
